import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Phone, X, ArrowUpRight } from "lucide-react";
import { CLINIC } from "@/data/content";

const links = [
  { label: "About",    to: "#story",    type: "hash" },
  { label: "Services", to: "#services", type: "hash" },
  { label: "Why us",   to: "#why",      type: "hash" },
  { label: "Journal",  to: "/blog",     type: "route" },
  { label: "Contact",  to: "#find",     type: "hash" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hrefFor = (l) => (l.type === "hash" ? (onHome ? l.to : `/${l.to}`) : l.to);

  const NavItem = ({ l, cls = "" }) => {
    if (l.type === "route") return <Link to={l.to} className={cls} data-testid={`nav-link-${l.label.toLowerCase()}`}>{l.label}</Link>;
    return <a href={hrefFor(l)} className={cls} data-testid={`nav-link-${l.label.toLowerCase()}`}>{l.label}</a>;
  };

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-40 bg-[hsl(var(--primary-light))] text-[hsl(var(--primary-dark))] text-[12px] py-2 hidden md:block">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`mailto:${CLINIC.email}`} className="flex items-center gap-2 hover:underline"><span>✉</span> {CLINIC.email}</a>
            <a href={`tel:${CLINIC.phoneDial[0]}`} className="flex items-center gap-2 hover:underline"><Phone size={12} /> {CLINIC.phones[0]}</a>
          </div>
          <div className="opacity-80">Mon – Sat · 10:00 – 20:00</div>
        </div>
      </div>

      <motion.header
        initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1], delay: 0.2 }}
        className={`fixed top-0 md:top-[32px] inset-x-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-white/90 backdrop-blur-lg shadow-[0_1px_20px_-8px_rgba(23,64,93,0.15)]" : "bg-white/70 backdrop-blur"
        }`}
        data-testid="site-navbar"
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 md:px-12 h-[76px]">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/assets/logo.png" alt="Calm Dentistry & Cosmetology" className="h-8 md:h-10 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => <NavItem key={l.to} l={l} cls="text-[14px] font-medium text-[hsl(var(--ink))] hover:text-[hsl(var(--primary))] transition-colors" />)}
          </nav>

          <div className="flex items-center gap-3">
            <a href={onHome ? "#book" : "/#book"} className="btn-pill btn-solid hidden md:inline-flex" data-testid="nav-book-cta">
              Book appointment
              <span className="arrow"><ArrowUpRight size={16} strokeWidth={2} /></span>
            </a>
            <a href={onHome ? "#book" : "/#book"} className="md:hidden btn-pill btn-solid !py-2 !px-4 text-[12px]" data-testid="nav-book-cta-mobile">
              Book
              <span className="arrow" style={{width:24,height:24}}><ArrowUpRight size={13} /></span>
            </a>
            <button className="md:hidden p-2 text-[hsl(var(--ink))]" onClick={() => setOpen(true)} data-testid="nav-mobile-open" aria-label="Open menu">
              <Menu size={22} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-white text-[hsl(var(--ink))]"
            initial={{ clipPath: "circle(0% at 90% 5%)" }}
            animate={{ clipPath: "circle(150% at 90% 5%)" }}
            exit={{ clipPath: "circle(0% at 90% 5%)" }}
            transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center h-[72px] px-6">
              <img src="/assets/logo.png" alt="Calm Dentistry & Cosmetology" className="h-9 w-auto" />
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" aria-label="Close menu"><X size={22} /></button>
            </div>
            <nav className="flex flex-col gap-5 px-6 mt-12">
              {links.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.06 }}>
                  <NavItem l={l} cls="font-display text-4xl block" />
                </motion.div>
              ))}
              <a href={onHome ? "#book" : "/#book"} onClick={() => setOpen(false)} className="btn-pill btn-solid mt-6 self-start">
                Book appointment <span className="arrow"><ArrowUpRight size={16} /></span>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
